import type { MaybeRefOrGetter } from 'vue';
import type { AnyItem, CompositePrimaryKey, RelationOperationToOne, RelationOperationToOneInCreate, RelationOperationToOneInUpdate, ResolvedMonospaceCollection, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import { pickNonSpecialProps } from '@rstore/shared';
import { computed, toValue } from 'vue';
import { useDisplayTemplateFieldSelection } from '#layers/blocks/composables/use-display-template-field-selection';
import { useItems } from '#layers/collection-query/public';
import { getCollectionPrimaryKeyFields, getItemPrimaryKey, isConnectToOne, isCreateToOne, isDisconnectToOne, isEmptyPrimaryKey, isUpdateToOne } from '#layers/data-model/public';
import { injectFormBlockContext } from '#layers/forms/public';

function findItemByPrimaryKey(
	items: AnyItem[],
	key: CompositePrimaryKey,
	collection: ResolvedMonospaceCollection,
) {
	const pkFields = getCollectionPrimaryKeyFields(collection);

	return items.find((item) => {
		const itemKey = getItemPrimaryKey(item, collection);

		return pkFields.every((field) => itemKey[field.apiName] === key[field.apiName]);
	});
}

export const useM2OState = (
	field: MaybeRefOrGetter<ResolvedMonospaceSingleRelationField | null>,
	referencedItemKey: MaybeRefOrGetter<CompositePrimaryKey | null>,
	fieldValue: MaybeRefOrGetter<RelationOperationToOne | null>,
) => {
	const collection = computed(() => toValue(field)?.oppositeCollection);
	const formBlockContext = injectFormBlockContext(null);
	const isCreateForm = computed(() => formBlockContext?.isCreate.value ?? false);

	const effectiveKey = computed<CompositePrimaryKey | null>(() => {
		let key = toValue(referencedItemKey);

		if (isCreateForm.value) {
			const _fieldValue = toValue(fieldValue) as RelationOperationToOneInCreate | null;

			if (_fieldValue && '_connect' in _fieldValue) {
				key = _fieldValue._connect.key;
			}
			else if (_fieldValue && 'disconnect' in _fieldValue) {
				key = null;
			}
		}
		else {
			const _fieldValue = toValue(fieldValue) as RelationOperationToOneInUpdate | null;
			const connectOperation = Array.isArray(_fieldValue) ? _fieldValue.find((op) => isConnectToOne(op)) : null;
			const disconnectOperation = Array.isArray(_fieldValue) ? _fieldValue.find((op) => isDisconnectToOne(op)) : null;

			if (disconnectOperation) {
				key = null;
			}

			if (connectOperation) {
				key = connectOperation._connect.key;
			}
		}

		return key;
	});

	const isValidKey = computed(() => !isEmptyPrimaryKey(effectiveKey.value));

	const filter = computed(() => {
		const key = effectiveKey.value;

		if (!key || isEmptyPrimaryKey(key)) {
			return;
		}

		return {
			_and: Object.keys(key).map((keyName: string) => {
				return { [keyName]: { _eq: key[keyName] } };
			}),
		};
	});

	const fieldSelection = useDisplayTemplateFieldSelection(collection);

	const { items, isLoading: isItemsLoading, refresh } = useItems({
		enabled: isValidKey,
		collection,
		query: () => ({ filter: filter.value, fields: fieldSelection.value }),
	});

	const isResolvingConnection = computed(() => {
		if (!isCreateForm.value || !collection.value) {
			return false;
		}

		const value = toValue(fieldValue) as RelationOperationToOneInCreate | null;

		if (!value || !isConnectToOne(value)) {
			return false;
		}

		return !findItemByPrimaryKey(items.value, value._connect.key, collection.value);
	});

	const isLoading = computed(() => isItemsLoading.value || isResolvingConnection.value);

	// Autorefresh on submit - fetch fresh data after save
	if (formBlockContext) {
		formBlockContext.state.value?.$onSuccess?.(() => {
			refresh();
		});
	}

	const item: AnyItem = computed(() => {
		if (!toValue(fieldValue)) {
			if (isValidKey.value) {
				return items.value[0];
			}
			else {
				return null;
			}
		}

		// In-Create
		if (isCreateForm.value) {
			const _fieldValue: RelationOperationToOneInCreate | null = toValue(fieldValue) as RelationOperationToOneInCreate;

			if (isCreateForm.value && isCreateToOne(_fieldValue)) {
				return _fieldValue._create.data;
			}
			else if (isConnectToOne(_fieldValue)) {
				return findItemByPrimaryKey(items.value, _fieldValue._connect.key, collection.value!);
			}
		}

		// In-Update
		else {
			const _fieldValue: RelationOperationToOneInUpdate = toValue(fieldValue) as RelationOperationToOneInUpdate ?? [];

			const createOperation = Array.isArray(_fieldValue) ? _fieldValue.find((op) => isCreateToOne(op)) : null;
			const connectOperation = Array.isArray(_fieldValue) ? _fieldValue.find((op) => isConnectToOne(op)) : null;
			const updateOperation = Array.isArray(_fieldValue) ? _fieldValue.find((op) => isUpdateToOne(op)) : null;
			const disconnectOperation = Array.isArray(_fieldValue) ? _fieldValue.find((op) => isDisconnectToOne(op)) : null;

			let finalItem = items.value[0] ?? null;

			if (createOperation) {
				finalItem = createOperation._create.data;
			}

			if (updateOperation) {
				finalItem = { ...pickNonSpecialProps(finalItem), ...updateOperation._update.data };
			}

			if (disconnectOperation && !createOperation && !connectOperation) {
				finalItem = null;
			}

			return finalItem;
		}
	});

	const isCreate = computed(() => {
		const _fieldValue = toValue(fieldValue);
		return Array.isArray(_fieldValue) ? _fieldValue.some((op) => isCreateToOne(op)) : isCreateToOne(toValue(fieldValue));
	});
	const isUpdate = computed(() => {
		const _fieldValue = toValue(fieldValue);
		return Array.isArray(_fieldValue) ? _fieldValue.some((op) => isUpdateToOne(op)) : isUpdateToOne(toValue(fieldValue));
	});

	return { collection, item, isCreate, isUpdate, isLoading };
};
